/**
 * ScopedFinancialReport — clone of the main admin Reports tab UI, scoped to
 * the calling operator's location or cashier's sub-group.
 *
 * Backed by /api/operator/reports/financial (operator scope) or
 * /api/cashier/reports/financial (cashier scope). Renders 4 period cards
 * (Today / Week / Month / Year) with breakdowns for Sports, Lottery,
 * Casino, Slots, and a combined cash-profit roll-up.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { RefreshCw, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => Number(n || 0).toFixed(2);
const sign = (n) => (Number(n || 0) >= 0 ? '+' : '');

const PERIODS = [
    { key: 'today', label: 'Today' },
    { key: 'week',  label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'year',  label: 'This Year' },
];

const ScopedFinancialReport = ({ endpoint = '/cashier/reports/financial' }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}${endpoint}`, { withCredentials: true });
            setData(data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [endpoint]);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) {
        return (
            <div className="text-center py-12 text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading financial report…
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="space-y-4" data-testid="scoped-financial-report">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-base font-semibold text-zinc-100">Financial breakdown</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">
                        {data.player_count} players in scope · Mon–Sun (America/Chicago)
                    </p>
                </div>
                <Button onClick={load} size="sm" variant="outline"
                    className="h-8 border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                    data-testid="financial-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {PERIODS.map(({ key, label }) => {
                    const p = data[key] || {};
                    return <PeriodCard key={key} label={label} p={p} testKey={key} />;
                })}
            </div>
        </div>
    );
};

const PeriodCard = ({ label, p, testKey }) => {
    const combined = p.combined || {};
    const sports = p.sports || {};
    const lottery = p.lottery || {};
    const casino = p.casino || {};
    const slots = p.slots || {};
    const blackjack = p.blackjack || {};
    const tcp = p.three_card_poker || {};
    const craps = p.craps || {};
    const pending = p.pending_cashouts || { total: 0, count: 0 };
    const fp = p.free_play || { total: 0 };

    const cashProfit = combined.cash_profit ?? 0;
    const profitClass = cashProfit >= 0 ? 'text-emerald-300' : 'text-rose-300';

    const split = p.profit_split || {};

    // Always show Casino and Slots — the cashier needs to see "0 topups" too
    // so they can confirm a quiet period vs. a missing-data bug.
    const showCasino = true;
    const showSlots = (slots.wagered || 0) > 0 || (slots.count || 0) > 0 || (slots.jackpot_paid || 0) > 0;
    const showBlackjack = (blackjack.wagered || 0) > 0 || (blackjack.rounds || 0) > 0;
    const showTcp = (tcp.wagered || 0) > 0 || (tcp.rounds || 0) > 0;
    const showCraps = (craps.wagered || 0) > 0 || (craps.rolls || 0) > 0;

    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-900/30 p-4 space-y-2"
            data-testid={`period-card-${testKey}`}>
            <h4 className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold">{label}</h4>

            {/* Money in/out */}
            <Row label="Loaded in" right={`+${fmt(p.deposits?.total)}`} rightClass="text-emerald-300" />
            <Row label="Redeemed out" right={`-${fmt(p.withdrawals?.total)}`} rightClass="text-rose-300" />
            {pending.count > 0 && (
                <Row label={<>Pending <Badge>{pending.count}</Badge></>}
                    right={`~${fmt(pending.total)}`} rightClass="text-amber-300" />
            )}
            <Row label="Free play given" right={`-${fmt(fp.total)}`} rightClass="text-purple-300" />

            {/* Sports */}
            <Section title="Sports" color="text-sky-300">
                <Row label="Wagered / Paid"
                    right={`${fmt(sports.wagered)} / ${fmt(sports.paid_out)}`} small />
                <Row label="Bets (W/L)"
                    right={`${sports.count || 0} (${sports.won || 0}W/${sports.lost || 0}L)`} small />
            </Section>

            {/* Lottery */}
            <Section title="Lottery" color="text-amber-300">
                <Row label="Wagered / Paid"
                    right={`${fmt(lottery.wagered)} / ${fmt(lottery.paid_out)}`} small />
                <Row label="Tickets (W/L)"
                    right={`${lottery.count || 0} (${lottery.won || 0}W/${lottery.lost || 0}L)`} small />
            </Section>

            {/* Casino */}
            {showCasino && (
                <Section title="Slots" color="text-emerald-300">
                    <Row label="Topups / Redeems"
                        right={`${fmt(casino.topup_total)} / ${fmt(casino.redeem_total)}`} small />
                    <Row label="Net gross"
                        right={`${sign(casino.gross)}${fmt(casino.gross)}`}
                        rightClass={
                            (casino.gross || 0) > 0 ? 'text-emerald-300'
                            : (casino.gross || 0) < 0 ? 'text-rose-300'
                            : 'text-zinc-400'   /* neutral at $0 so green doesn't mislead at-a-glance */
                        } small />
                </Section>
            )}

            {/* Slots */}
            {showSlots && (
                <Section title="Slots" color="text-pink-300">
                    <Row label="Wagered / Paid"
                        right={`${fmt(slots.wagered)} / ${fmt(slots.paid_out)}`} small />
                    <Row label="Spins (W/L)"
                        right={`${slots.count || 0} (${slots.won || 0}W/${slots.lost || 0}L)`} small />
                    {(slots.jackpot_paid || 0) > 0 && (
                        <Row label="Jackpots paid"
                            right={`-${fmt(slots.jackpot_paid)}`}
                            rightClass="text-pink-300 font-bold" small />
                    )}
                </Section>
            )}

            {/* Blackjack (in-house) */}
            {showBlackjack && (
                <Section title="Blackjack" color="text-amber-300">
                    <Row label="Wagered / Paid"
                        right={`${fmt(blackjack.wagered)} / ${fmt(blackjack.paid_out)}`} small />
                    <Row label="Hands (W/L/P)"
                        right={`${blackjack.count || 0} (${blackjack.won || 0}W/${blackjack.lost || 0}L${(blackjack.push || 0) > 0 ? `/${blackjack.push}P` : ''})`} small />
                    <Row label="Net"
                        right={`${(blackjack.profit || 0) >= 0 ? '+' : ''}${fmt(blackjack.profit)}`}
                        rightClass={(blackjack.profit || 0) >= 0 ? 'text-emerald-300 font-bold' : 'text-rose-300 font-bold'} small />
                </Section>
            )}

            {/* Three Card Poker (in-house) */}
            {showTcp && (
                <Section title="3-Card Poker" color="text-cyan-300">
                    <Row label="Wagered / Paid"
                        right={`${fmt(tcp.wagered)} / ${fmt(tcp.paid_out)}`} small />
                    <Row label="Rounds (W/L/P)"
                        right={`${tcp.rounds || 0} (${tcp.won || 0}W/${tcp.lost || 0}L${(tcp.push || 0) > 0 ? `/${tcp.push}P` : ''})`} small />
                    {(tcp.folds || 0) > 0 && (
                        <Row label="Folds"
                            right={`${tcp.folds}`}
                            rightClass="text-rose-300" small />
                    )}
                    <Row label="Net"
                        right={`${(tcp.profit || 0) >= 0 ? '+' : ''}${fmt(tcp.profit)}`}
                        rightClass={(tcp.profit || 0) >= 0 ? 'text-emerald-300 font-bold' : 'text-rose-300 font-bold'} small />
                </Section>
            )}

            {/* Craps (in-house) */}
            {showCraps && (
                <Section title="Craps" color="text-emerald-300">
                    <Row label="Wagered / Paid"
                        right={`${fmt(craps.wagered)} / ${fmt(craps.paid_out)}`} small />
                    <Row label="Sessions · Rolls"
                        right={`${craps.sessions || 0} · ${craps.rolls || 0}`} small />
                    <Row label="Net"
                        right={`${(craps.profit || 0) >= 0 ? '+' : ''}${fmt(craps.profit)}`}
                        rightClass={(craps.profit || 0) >= 0 ? 'text-emerald-300 font-bold' : 'text-rose-300 font-bold'} small />
                </Section>
            )}

            {/* Combined */}
            <div className="border-t border-zinc-800 pt-2 mt-1 space-y-1">
                <Row label="Bets wagered" right={fmt(combined.total_wagered)} small />
                <Row label="Bets paid out" right={`-${fmt(combined.total_paid)}`} rightClass="text-rose-300" small />
                <div className="border-t border-zinc-700 pt-1.5 mt-1 flex items-center justify-between gap-3">
                    <div className="flex flex-col">
                        <span className="text-xs uppercase tracking-wider text-zinc-300 font-semibold">Net after free play (not profit)</span>
                        <span className="text-[10px] text-zinc-500">loaded − redeemed − free play</span>
                    </div>
                    <span className={`font-mono font-bold text-base ${profitClass}`}>
                        {sign(cashProfit)}{fmt(cashProfit)}
                    </span>
                </div>
                <Row label="Bet margin" right={`${sign(combined.bet_margin)}${fmt(combined.bet_margin)}`}
                    rightClass={(combined.bet_margin || 0) >= 0 ? 'text-emerald-300' : 'text-rose-300'} small />

                {/* Operator-share split — cashier-only. Operators get the
                    per-cashier breakdown on the dedicated Profit tab. */}
                {cashProfit > 0 && split.applies && (
                    <div className="border-t border-zinc-700 pt-2 mt-1 space-y-1"
                        data-testid={`profit-split-${testKey}`}>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                            Profit split — {Math.round((split.operator_share_pct || 0.80) * 100)}% to operator / {Math.round((split.cashier_keep_pct || 0.20) * 100)}% kept
                        </p>
                        <Row label="You owe operator"
                            right={`+${fmt(split.operator_owed)}`}
                            rightClass="text-amber-300 font-bold" small />
                        <Row label="You keep"
                            right={`+${fmt(split.cashier_keep)}`}
                            rightClass="text-cyan-300 font-bold" small />
                    </div>
                )}
            </div>
        </div>
    );
};

const Row = ({ label, right, rightClass = 'text-zinc-100', small }) => (
    <div className={`flex items-center justify-between ${small ? 'text-xs' : 'text-sm'}`}>
        <span className="text-zinc-500">{label}</span>
        <span className={`font-mono ${rightClass}`}>{right}</span>
    </div>
);

const Section = ({ title, color, children }) => (
    <div className="border-t border-zinc-800 pt-2 mt-1">
        <p className={`text-[10px] uppercase tracking-widest font-semibold mb-1 ${color}`}>{title}</p>
        <div className="space-y-0.5">{children}</div>
    </div>
);

const Badge = ({ children }) => (
    <span className="ml-1 text-[9px] uppercase tracking-widest font-medium text-amber-300 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0 rounded">
        {children}
    </span>
);

export default ScopedFinancialReport;
